import React, { Suspense, lazy, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';
// the async-light build ships no grammars: each language is fetched the first
// time a code block uses it, instead of bundling all ~300 of them
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { CheckIcon, CopyIcon } from './icons';

// Mermaid is ~450KB gzipped, and most answers contain no diagram. Loaded on
// first use so everyone else never pays for it.
const MermaidDiagram = lazy(() => import('../MermaidDiagram'));

// Markdown for assistant answers. The primitive is assistant-ui's own: it
// memoizes finished blocks and defers parsing, so a long answer doesn't
// re-parse on every streamed token the way plain react-markdown would.
export default function Markdown() {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="chat-markdown"
      components={{ SyntaxHighlighter: CodeBlock, CodeHeader }}
      componentsByLanguage={{
        mermaid: { SyntaxHighlighter: MermaidBlock, CodeHeader: MermaidHeader },
        svg: { SyntaxHighlighter: SvgBlock },
        json: { SyntaxHighlighter: JsonBlock },
        csv: { SyntaxHighlighter: CsvBlock },
        tsv: { SyntaxHighlighter: CsvBlock },
      }}
    />
  );
}

// Markdown for a reasoning summary. The same primitive, deliberately a lot
// less: a summary is prose with bold run-ins and the odd list, never a diagram
// or a syntax-highlighted block, and it has to stay visually quieter than the
// answer it sits above.
export function ReasoningMarkdown() {
  return <MarkdownTextPrimitive remarkPlugins={[remarkGfm]} className="thinking-markdown" />;
}

function CodeBlock({ language, code }) {
  return (
    <SyntaxHighlighter
      language={language || 'text'}
      style={oneLight}
      customStyle={{ margin: 0, background: 'transparent', padding: '0.75rem 1rem', fontSize: '0.875rem' }}
      wrapLongLines
    >
      {code}
    </SyntaxHighlighter>
  );
}

function CodeHeader({ language, code }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // a blocked clipboard is not worth an error state
    }
  };

  return (
    <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
      <span>{language || 'text'}</span>
      <button
        type="button"
        onClick={copy}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-200 hover:text-gray-700"
        aria-label="Copy code"
      >
        {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

// Tabular data is a table. The fence is the worst possible way to read it.
function CsvBlock({ code, language }) {
  const [showSource, setShowSource] = useState(false);

  const rows = useMemo(() => parseDelimited(code, language === 'tsv' ? '\t' : ','), [code, language]);

  // one row is a header with nothing under it, which usually means the answer
  // is still streaming
  if (rows.length < 2) {
    return <CodeBlock code={code} language={language} />;
  }

  if (showSource) {
    return (
      <div>
        <SourceToggle showing onClick={() => setShowSource(false)} label="Show table" />
        <CodeBlock code={code} language={language} />
      </div>
    );
  }

  const [header, ...body] = rows;

  return (
    <div>
      <SourceToggle showing={false} onClick={() => setShowSource(true)} />

      <div className="max-h-96 overflow-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              {header.map((cell, i) => (
                <th key={i} className="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 font-semibold text-gray-700">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r} className="even:bg-gray-50/50">
                {header.map((_, c) => (
                  <td key={c} className="border-b border-gray-100 px-3 py-1.5 align-top text-gray-800">
                    {row[c] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A real parser rather than split(","): quoted fields carry commas, newlines
// and doubled quotes, and a model writing addresses will produce all three.
function parseDelimited(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }

      continue;
    }

    switch (char) {
      case '"':
        quoted = true;
        break;

      case delimiter:
        row.push(field.trim());
        field = '';
        break;

      case '\r':
        break;

      case '\n':
        row.push(field.trim());
        rows.push(row);
        row = [];
        field = '';
        break;

      default:
        field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field.trim());
    rows.push(row);
  }

  // a trailing newline leaves an empty last row
  return rows.filter((r) => r.some((cell) => cell !== ''));
}

// An SVG is a picture, so show the picture. Sanitised first: SVG carries
// <script> and event handlers, and this one was written by a model.
function SvgBlock({ code, language }) {
  const [showSource, setShowSource] = useState(false);

  const clean = useMemo(() => {
    if (!looksComplete(code)) return null;

    return DOMPurify.sanitize(code, {
      USE_PROFILES: { svg: true, svgFilters: true },
    });
  }, [code]);

  // still streaming, or nothing survived sanitising
  if (!clean) {
    return <CodeBlock code={code} language={language} />;
  }

  if (showSource) {
    return (
      <div>
        <SourceToggle showing onClick={() => setShowSource(false)} />
        <CodeBlock code={code} language={language} />
      </div>
    );
  }

  return (
    <div>
      <SourceToggle showing={false} onClick={() => setShowSource(true)} />
      {/* bounded: a model can emit a viewBox that would otherwise fill the page */}
      <div
        className="flex max-h-96 justify-center overflow-auto p-3 [&>svg]:h-auto [&>svg]:max-w-full"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
}

// A preview is an interpretation; the source is what the model actually wrote,
// and when the two disagree you need to see it.
function SourceToggle({ showing, onClick, label = 'Show image' }) {
  return (
    <div className="flex justify-end border-b border-gray-100 px-2 py-1">
      <button
        type="button"
        onClick={onClick}
        className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      >
        {showing ? label : 'View source'}
      </button>
    </div>
  );
}

// Models emit minified JSON often enough that a 2000-character single line is
// worth re-indenting. Invalid or half-streamed JSON is left exactly as it came.
function JsonBlock({ code, language }) {
  const pretty = useMemo(() => {
    const trimmed = code.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return code;

    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return code;
    }
  }, [code]);

  return <CodeBlock code={pretty} language={language} />;
}

// Mermaid can't render half a diagram: mid-stream the fence is incomplete and
// the parser throws. Wait for the block to look finished, and show the source
// until then so the user sees something arriving.
function MermaidBlock({ code }) {
  if (!looksComplete(code)) {
    return (
      <pre className="overflow-x-auto px-4 py-3 text-xs text-gray-500">
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <Suspense fallback={<div className="px-4 py-3 text-xs text-gray-400">rendering diagram...</div>}>
      <MermaidDiagram chart={code} className="px-2 py-3" />
    </Suspense>
  );
}

function MermaidHeader() {
  return (
    <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
      diagram
    </div>
  );
}

// A heuristic, not a parser: a diagram needs a type line and at least one
// statement after it. Good enough to stop mermaid throwing on every keystroke.
function looksComplete(code) {
  const lines = code.trim().split('\n').filter((line) => line.trim() !== '');

  return lines.length >= 2;
}
